package one.healthium.ui.compose.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Stable
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.sp


@Suppress("PropertyName")
@Stable
class AppTextStyle(
    private val fontFamily: FontFamily? = null,
    private val fontFamilySecondary: FontFamily? = null,
    private val fontFamilyTertiary: FontFamily? = null,
    val body: TextStyle = TextStyle(
        fontFamily = fontFamilySecondary,
        fontSize = 18.sp,
        fontWeight = FontWeight.Normal,
    ),
    val bodySemiBold: TextStyle = TextStyle(
        fontFamily = fontFamilySecondary,
        fontSize = 18.sp,
        fontWeight = FontWeight.SemiBold,
    ),
    val title20Bold: TextStyle = TextStyle(
        fontFamily = fontFamilySecondary,
        fontSize = 20.sp,
        fontWeight = FontWeight.Bold,
    ),
    val secondary_22_700: TextStyle = TextStyle(
        fontFamily = fontFamilySecondary,
        fontSize = 22.sp,
        fontWeight = FontWeight.Bold,
    ),
    val titleExtraBold: TextStyle = TextStyle(
        fontFamily = fontFamilySecondary,
        fontSize = 22.sp,
        fontWeight = FontWeight.ExtraBold,
    ),
    val description: TextStyle = TextStyle(
        fontFamily = fontFamilySecondary,
        fontSize = 17.sp,
        fontWeight = FontWeight.Normal,
    ),
    val textField: TextStyle = TextStyle(
        fontFamily = fontFamily,
        fontSize = 38.sp,
        fontWeight = FontWeight.SemiBold,
    ),
    val button: TextStyle = TextStyle(
        fontFamily = fontFamily,
        fontSize = 16.sp,
        fontWeight = FontWeight.Bold,
        textAlign = TextAlign.Center,
    ),
    val section: TextStyle = TextStyle(
        fontFamily = fontFamily,
        fontSize = 17.sp,
        fontWeight = FontWeight.Medium,
    ),
    val sectionSmall: TextStyle = TextStyle(
        fontFamily = fontFamily,
        fontSize = 14.sp,
        fontWeight = FontWeight.Medium,
    )
) {
    val textButton
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 16.sp,
            fontWeight = FontWeight.Medium,
        )
    val textButtonGray
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 16.sp,
            fontWeight = FontWeight.W400,
            color = gray_4,
        )
    val titleLarge
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 38.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val title
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 32.sp,
            fontWeight = FontWeight.Bold,
        )
    val titleSemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 32.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val titleMedium
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 30.sp,
            fontWeight = FontWeight.Medium,
        )
    val titleSmallBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 22.sp,
            fontWeight = FontWeight.Bold,
        )
    val subTitleLarge
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 20.sp,
            fontWeight = FontWeight.Medium,
        )
    val subTitle
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 18.sp,
            fontWeight = FontWeight.Normal,
            lineHeight = 24.sp,
        )
    val subTitleBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
        )
    val subTitleSemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 18.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val headerRegular
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 24.sp,
            fontWeight = FontWeight.Normal,
        )
    val header
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 24.sp,
            fontWeight = FontWeight.Bold,
        )
    val headerSemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 24.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val body1
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 16.sp,
            fontWeight = FontWeight.Normal,
        )
    val body1Medium
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 16.sp,
            fontWeight = FontWeight.Medium,
        )
    val body1Bold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold,
        )
    val body2
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 14.sp,
            fontWeight = FontWeight.Normal,
        )
    val body2Medium
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 14.sp,
            fontWeight = FontWeight.Medium,
        )
    val body3
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 17.sp,
            fontWeight = FontWeight.Normal,
        )
    val caption
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 14.sp,
            fontWeight = FontWeight.Normal,
        )
    val captionSemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 14.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val captionBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 14.sp,
            fontWeight = FontWeight.Bold,
        )
    val subline
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 12.sp,
            fontWeight = FontWeight.Normal,
        )
    val sublineMedium
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 12.sp,
            fontWeight = FontWeight.Medium,
        )
    val sublineBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
        )
    val default10Medium
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 10.sp,
            fontWeight = FontWeight.Medium,
        )
    val default10ExtraBold
        @Composable get() = TextStyle(
            fontFamily = fontFamily,
            fontSize = 10.sp,
            fontWeight = FontWeight.ExtraBold,
        )
    val secondary_9_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 9.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_12_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 12.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_12_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val secondary_12_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
        )
    val secondary_14_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 14.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_14_500
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 14.sp,
            fontWeight = FontWeight.Medium,
        )
    val secondary_14_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 14.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins14Bold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 14.sp,
            fontWeight = FontWeight.Bold,
        )
    val secondary_16_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 16.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_16_500
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 16.sp,
            fontWeight = FontWeight.Medium,
        )
    val secondary_16_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold,
        )
    val secondary_17_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 17.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_17_500
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 17.sp,
            fontWeight = FontWeight.Medium,
        )
    val secondary_17_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 17.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins17Bold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 17.sp,
            fontWeight = FontWeight.Bold,
        )
    val secondary_18_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 18.sp,
            fontWeight = FontWeight.Normal,
        )
    val poppins18Medium
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 18.sp,
            fontWeight = FontWeight.Medium,
        )
    val poppins18SemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 18.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val secondary_18_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 18.sp,
            fontWeight = FontWeight.Bold,
        )
    val poppins20
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 20.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_20_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 20.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val secondary_20_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 20.sp,
            fontWeight = FontWeight.Bold,
        )
    val poppins22SemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 22.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins24
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 24.sp,
            fontWeight = FontWeight.Normal,
        )
    val secondary_24_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 24.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins26SemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 26.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins26Bold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 26.sp,
            fontWeight = FontWeight.Bold,
        )
    val secondary_30_500
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 30.sp,
            fontWeight = FontWeight.Medium,
        )
    val poppins30SemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 30.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins30Bold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 30.sp,
            fontWeight = FontWeight.Bold,
        )
    val poppins32
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 32.sp,
            fontWeight = FontWeight.Bold,
        )

    val poppins32SemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 32.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val secondary_32_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 32.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val secondary_32_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 32.sp,
            fontWeight = FontWeight.Bold,
        )
    val secondary_36_600
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 36.sp,
            fontWeight = FontWeight.SemiBold,
        )
    val poppins40SemiBold
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 40.sp,
            fontWeight = FontWeight.SemiBold,
        )

    val secondary_64_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilySecondary,
            fontSize = 64.sp,
            fontWeight = FontWeight.Bold,
        )

    val tertiary14_700
        @Composable get() = TextStyle(
            fontFamily = fontFamilyTertiary,
            fontSize = 14.sp,
            fontWeight = FontWeight.Bold,
        )

    val tertiary18_400
        @Composable get() = TextStyle(
            fontFamily = fontFamilyTertiary,
            fontSize = 18.sp,
            fontWeight = FontWeight.Normal,
        )
}